/**
 * Per-product config registry — single source of truth for product traits.
 * See issue #123.
 *
 * Usage:
 *   import { getProduct, IDE_PRODUCTS, KED_PRODUCTS } from "data/products/index";
 *   const cfg = getProduct("IJ");  // { code: "IJ", label: "IntelliJ IDEA", isIDE: true, ... }
 */
import {RELEASES_SETS} from "../constants";
import AS from "./ai-assistant";
import IH from "./innovation-hub";
import AMP from "./amper";
import AQ from "./aqua";
import CL from "./clion";
import CLN from "./clion-nova";
import CLT from "./client";
import CPPT from "./cpp-tools";
import CWM from "./code-with-me";
import CC from "./codecanvas";
import CMP from "./compose-multiplatform";
import DG from "./datagrip";
import DL from "./datalore";
import DBAO from "./databao";
import DS from "./dataspell";
import DC from "./dotcover";
import DM from "./dotmemory";
import DP from "./dotpeek";
import DT from "./dottrace";
import FL from "./fleet";
import GW from "./gateway";
import GO from "./goland";
import GZ from "./grazie";
import HB from "./hub";
import IS from "./ide-services";
import IJ from "./intellij-idea";
import JB from "./jetbrains";
import JN from "./junie";
import DN from "./jetbrains-net";
import DNT from "./dotnet-tools";
import DU from "./dotultimate";
import EDU from "./jetbrains-academy";
import AIR from "./jetbrains-air";
import AI from "./jetbrains-ai";
import GDV from "./jetbrains-game-dev";
import IDEs from "./jetbrains-ides";
import MP from "./jetbrains-marketplace";
import KT from "./kotlin";
import KM from "./kotlin-multiplatform";
import KTR from "./ktor";
import EXP from "./exposed";
import ML from "./mellum";
import MPS from "./mps";
import PS from "./phpstorm";
import PC from "./pycharm";
import PCD from "./pycharm-data";
import JBD from "./jetbrains-for-data";
import KG from "./koog";
import QD from "./qodana";
import RS from "./resharper";
import RScpp from "./resharper-cpp";
import RD from "./rider";
import RM from "./rubymine";
import RR from "./rustrover";
import REM from "./remote-development";
import ST from "./startup-program";
import TC from "./teamcity";
import TB from "./toolbox-app";
import UNI from "./university-programs";
import WS from "./webstorm";
import WRS from "./writerside";
import YT from "./youtrack";

const RAW = {
  AS,
  IH,
  AMP,
  AQ,
  CL,
  CLN,
  CLT,
  CPPT,
  CWM,
  CC,
  CMP,
  DG,
  DL,
  DBAO,
  DS,
  DC,
  DM,
  DP,
  DT,
  FL,
  GW,
  GO,
  GZ,
  HB,
  IS,
  IJ,
  JB,
  JN,
  DN,
  DNT,
  DU,
  EDU,
  AI,
  AIR,
  GDV,
  IDEs,
  MP,
  KT,
  KM,
  KTR,
  EXP,
  ML,
  MPS,
  PS,
  PC,
  PCD,
  JBD,
  KG,
  QD,
  RS,
  RScpp,
  RD,
  RM,
  RR,
  REM,
  ST,
  TC,
  TB,
  UNI,
  WS,
  WRS,
  YT,
};

// Add `value` alias (= code) for @rescui/select compatibility. `slug` is
// authored per-file so there's only one place to edit when adding,
// renaming, or removing a product — the product's own file. All product
// objects work as both registry configs AND PRODUCTS entries.
const ALL_PRODUCTS_MAP = Object.fromEntries(
  Object.entries(RAW).map(([key, cfg]) => [key, {...cfg, value: cfg.code}])
);

/** All product configs as an array. */
export const ALL_PRODUCTS = Object.values(ALL_PRODUCTS_MAP);

// Sanity check at module load: every product must declare a logoFamily.
// Catches the "forgot to set logoFamily on a new product" footgun before
// runtime — getLogoUrl would otherwise throw deep inside a render tree
// with a less obvious source. The valid family names live in
// src/libs/logo.js (LOGO_FAMILIES); we don't import them here to avoid
// a cycle, so this check just verifies the field is present and a string.
for (const p of ALL_PRODUCTS) {
  if (typeof p.logoFamily !== "string" || p.logoFamily.length === 0) {
    throw new Error(
      `Product "${p.code}" (${p.slug}) is missing required logoFamily field. ` +
        `Set logoFamily in src/data/products/${p.slug}.js — see LOGO_FAMILIES in src/libs/logo.js for valid values.`
    );
  }
}

/** O(1) lookup by product code. Returns undefined if not found. */
export function getProduct(code) {
  return ALL_PRODUCTS_MAP[code];
}

/** O(1) lookup by product slug. Returns undefined if not found. */
const ALL_PRODUCTS_BY_SLUG = Object.fromEntries(ALL_PRODUCTS.map((p) => [p.slug, p]));
export function getProductBySlug(slug) {
  return ALL_PRODUCTS_BY_SLUG[slug];
}

// --- Derived arrays (replace scattered hardcoded arrays) ---

export const IDE_PRODUCTS = ALL_PRODUCTS.filter((p) => p.isIDE).map((p) => p.code);
export const KED_PRODUCTS = ALL_PRODUCTS.filter((p) => p.isKED).map((p) => p.code);
export const DOT_NET_PRODUCTS = ALL_PRODUCTS.filter((p) => p.isDotNet).map((p) => p.code);
export const PRODUCTS_HIDING_NAME = ALL_PRODUCTS.filter((p) => p.hideProductName).map(
  (p) => p.code
);
export const PRODUCTS_WITH_SUBTITLE = ALL_PRODUCTS.filter((p) => p.hasSubtitle).map((p) => p.code);
export const PRODUCTS_WITH_SUBTITLE2 = ALL_PRODUCTS.filter((p) => p.hasSubtitle2).map(
  (p) => p.code
);
export const PRODUCTS_WITH_GRID = ALL_PRODUCTS.filter((p) => p.hasGrid).map((p) => p.code);
export const PRODUCTS_WITH_BLACK_TEXT = ALL_PRODUCTS.filter((p) => p.hasBlackText).map(
  (p) => p.code
);
export const PRODUCTS_WITHOUT_LOGOS = ALL_PRODUCTS.filter((p) => p.hasLogo === false).map(
  (p) => p.code
);
export const PRODUCTS_WITH_MODUL_FONT = ALL_PRODUCTS.filter((p) => p.hasModulFont).map(
  (p) => p.code
);

// --- Derived maps (replace scattered maps in constants.js) ---

// The channels where a hasGrid product shows no grid.
export const NO_GRID_CHANNELS = Object.fromEntries(
  ALL_PRODUCTS.filter((p) => p.noGridChannels).map((p) => [p.code, p.noGridChannels])
);
export const GRID_COLORS = Object.fromEntries(
  ALL_PRODUCTS.filter((p) => p.gridColor).map((p) => [p.code, p.gridColor])
);
export const KRAKEN_DIRS = Object.fromEntries(
  ALL_PRODUCTS.filter((p) => p.krakenDir).map((p) => [p.code, p.krakenDir])
);
export const THUMBNAIL_COLORS = Object.fromEntries(
  ALL_PRODUCTS.filter((p) => p.thumbnailColor).map((p) => [p.code, p.thumbnailColor])
);

// --- Sets for O(1) .has() lookups in templates ---

export const KED_PRODUCTS_SET = new Set(KED_PRODUCTS);
export const HIDING_NAME_SET = new Set(PRODUCTS_HIDING_NAME);
export const SUBTITLE_SET = new Set(PRODUCTS_WITH_SUBTITLE);
export const SUBTITLE2_SET = new Set(PRODUCTS_WITH_SUBTITLE2);
export const GRID_SET = new Set(PRODUCTS_WITH_GRID);
export const BLACK_TEXT_SET = new Set(PRODUCTS_WITH_BLACK_TEXT);
export const WITHOUT_LOGOS_SET = new Set(PRODUCTS_WITHOUT_LOGOS);
export const MODUL_FONT_SET = new Set(PRODUCTS_WITH_MODUL_FONT);

// --- Helper functions ---

/**
 * Check if a product is an IDE. Handles the PS + php-annotated special case.
 * Drop-in replacement for checkIfIDE in libs/logo.js.
 */
export function checkIfIDE(productCode, contentType) {
  if (productCode === "PS" && contentType === "php-annotated") return false;
  return getProduct(productCode)?.isIDE ?? false;
}

// --- Releases "Set" filter, per product ---

/**
 * The sets one product offers in the Releases channel, in the channel's
 * declared order. A product with no `releasesSets` offers every set, which is
 * the behaviour every product had before the field existed.
 *
 * The channel order is authoritative, not the order the product file lists:
 * one place decides how the Set dropdown reads, and a product only subtracts
 * from it.
 */
export function releasesSetsForProduct(productCode) {
  return filterReleasesSets(productCode, getProduct(productCode)?.releasesSets);
}

/**
 * RELEASES_SETS narrowed to `allowed`, in the channel's order. `allowed`
 * null/undefined means "every set" — the pre-subset behaviour.
 *
 * Exported for the unit test: the typo guard below cannot be reached through
 * `releasesSetsForProduct` without a bad product file in the tree.
 */
export function filterReleasesSets(productCode, allowed) {
  if (!allowed) return RELEASES_SETS;
  const allowedSet = new Set(allowed);
  const sets = RELEASES_SETS.filter((set) => allowedSet.has(set.value));
  // A product that names a set the channel does not declare is a config typo,
  // and it would silently shorten the dropdown instead.
  if (sets.length !== allowed.length) {
    const unknown = allowed.filter((value) => !RELEASES_SETS.some((set) => set.value === value));
    throw new Error(
      `Product "${productCode}" declares unknown releases set(s): ${unknown.join(", ")} — ` +
        `see RELEASES_SETS in src/data/constants.js.`
    );
  }
  return sets;
}

/**
 * The brand-treatment variants a product's banner ships in, or null for the
 * common case of a product with one. Each is `{id, label, hint}`; `id` is what
 * `variantOption` stores and the `?variant=` URL param carries.
 *
 * `setValue` narrows the answer to one Releases set. A treatment restyles the
 * product's own banner, so it applies only where that banner is what renders:
 * a product may declare `variantSets` to name those sets, and a set outside
 * the list — JetBrains Air's Screenshot set is the shared mockup tool, with no
 * headline a treatment could set in another face — has no treatment axis and
 * gets the channel's flat layout row. Called without a set, the answer is the
 * product's declaration as a whole.
 */
export function variantsForProduct(productCode, setValue = undefined) {
  const product = getProduct(productCode);
  const variants = product?.variants ?? null;
  if (!variants || setValue === undefined || !product?.variantSets) return variants;
  return product.variantSets.includes(setValue) ? variants : null;
}

/**
 * Is a brand treatment drawn for this layout? A variant may declare `layouts`
 * to name the layouts its frames exist for — a pair the design never drew is
 * not a cell the grid may offer. A variant without the field holds on every
 * layout the policy grants the product.
 */
export function variantAllowsLayout(variant, layoutId) {
  const layouts = variant?.layouts;
  if (!Array.isArray(layouts)) return true;
  return layouts.includes(Number(layoutId));
}

/**
 * The treatments a product offers on one layout: its declared variants, minus
 * the pairs the design does not draw. Null when the product has no treatment
 * axis at all, the way `variantsForProduct` answers.
 */
export function variantsForLayout(productCode, setValue, layoutId) {
  const variants = variantsForProduct(productCode, setValue);
  if (!variants) return null;
  return variants.filter((variant) => variantAllowsLayout(variant, layoutId));
}

/**
 * May the toolbar offer the pixel mosaic overlay for this product? True
 * unless the product declares `pixelation: false` (JetBrains Air).
 */
export function allowsPixelation(productCode) {
  return getProduct(productCode)?.pixelation !== false;
}

/**
 * May the background selector offer the generative gradient for this product?
 * True unless the product declares `generativeBackground: false` — a product
 * whose design fixes the background family (JetBrains Air) opts out.
 */
export function allowsGenerativeBackground(productCode) {
  return getProduct(productCode)?.generativeBackground !== false;
}

/**
 * Does the background selector offer the generative gradient tile on a page
 * of this channel and product? Not on Ads, and not for a product that opts
 * out. The Livestream page has its own default tile in place of the gallery
 * (BgSelector). The Defaults panel lists the tile for the visibility setting
 * where the selector offers it, and the server accepts a hidden mark there
 * only.
 */
export function offersGenerativeTile(channel, productCode) {
  return channel !== "ads" && allowsGenerativeBackground(productCode);
}

/** The set a product's Releases page opens on — its first offered set. */
export function defaultReleasesSetForProduct(productCode) {
  return releasesSetsForProduct(productCode)[0];
}
